import { Component, OnInit, OnDestroy } from '@angular/core';
import { PaymentService, RazorpayResponse, VerifyRequest } from '../../services/payment.service';
import { TenantService } from '../../services/tenant.service';
import { ToastService } from '../../services/toast.service';
import { Router } from '@angular/router';
import { ConfirmationPopupService } from '../../services/confirmation-popup.service';

@Component({
  selector: 'app-pay',
  templateUrl: './pay.component.html',
  styleUrls: ['./pay.component.scss'],
  standalone: false
})
export class PayComponent implements OnInit, OnDestroy {
  loading = false;
  online = true;
  acknowledged = false;
  amount: number | null = null;
  presetAmounts = [199, 299, 499, 999];
  private onlineHandler = () => { this.online = true; };
  private offlineHandler = () => { this.online = false; };

  constructor(
    public tenantService: TenantService,
    private paymentService: PaymentService,
    private toastService: ToastService,
    private confirmation: ConfirmationPopupService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.online = typeof navigator !== 'undefined' ? (navigator as any).onLine : true;
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
  }

  selectAmount(value: number) {
    if (!this.online || this.loading) return;
    this.amount = value;
  }

  async payNow(amountInRupees: number) {
    if (!this.online) {
      this.toastService.info('You are offline — cannot proceed with payment.');
      return;
    }

    try {
      this.loading = true;

      const createResp = await this.paymentService.createOrder(amountInRupees);

      const options = {
        key: createResp.keyId,
        amount: createResp.amount,
        currency: createResp.currency,
        name: createResp.name ?? 'Kinvyt',
        order_id: createResp.orderId,
        handler: async (razorpayResponse: RazorpayResponse) => {
          try {
            this.loading = true;
            const verifyReq: VerifyRequest = {
              paymentId: razorpayResponse.razorpay_payment_id,
              orderId: razorpayResponse.razorpay_order_id,
              signature: razorpayResponse.razorpay_signature
            };
            const verifyResp = await this.paymentService.verifyPayment(verifyReq);

            if (verifyResp?.success) {
              // navigate to receipt page with order details
              const tx = razorpayResponse.razorpay_payment_id;
              const orderId = createResp?.orderId ?? null;
              const amount = amountInRupees ?? null;
              const route = this.tenantService.getTenantPrefixedPath('pay/receipt');
              this.router.navigate(route, { state: { orderId, txId: tx, amount } });
            } else {
              this.confirmation.open('Payment verification failed on server.', {}, 'Payment failed', { txId: razorpayResponse.razorpay_payment_id });
              console.error('Verify response', verifyResp);
            }
          } catch (err) {
            console.error('verify error', err);
            this.confirmation.open('Verification error — check console.', {}, 'Payment error');
          } finally {
            this.loading = false;
          }
        },
        modal: {
          ondismiss: () => {
            try {
              // show a confirmation popup for cancellation
              this.confirmation.open('The payment was cancelled. No transaction was processed.', {}, 'Payment cancelled');
            } catch (e) {
              // fallback to toast
              this.toastService.info('Payment was cancelled. No transaction was made.');
            }
          }
        }
      } as any;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('create order error', err);
      this.toastService.error('Could not proceed with the request. Please try again later.');
    } finally {
      this.loading = false;
    }
  }

  onSubmit(amountValue: string | number | null | undefined) {
    const raw = amountValue ?? '';
    const parsed = typeof raw === 'number' ? raw : parseInt((raw as string).trim(), 10);
    if (!parsed || parsed <= 0 || Number.isNaN(parsed)) {
      this.toastService.info('Please enter a valid amount in rupees (greater than 0).');
      return;
    }

    if (!this.acknowledged) {
      this.toastService.info('Please acknowledge that this is a gift transaction (no invoice) before proceeding.');
      return;
    }

    this.payNow(parsed);
  }
}
